'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Zap, Users, BarChart3, PhoneCall, Globe, MessageSquare, Calendar, Shield, ArrowRight, CheckCircle2, Star, ChevronDown, Menu, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';



const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Testimonials', href: '#testimonials' },
];

const FEATURES = [
  {
    id: 'f1',
    icon: PhoneCall,
    title: 'AI Receptionist',
    desc: 'Answers every call 24/7, qualifies leads, and books appointments — without hiring staff.',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    span: 'col-span-2 row-span-1',
  },
  {
    id: 'f2',
    icon: Users,
    title: 'Lead Management',
    desc: 'Capture, score, and nurture leads from every channel in one unified CRM pipeline.',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    span: 'col-span-1 row-span-2',
  },
  {
    id: 'f3',
    icon: BarChart3,
    title: 'Live Analytics',
    desc: 'Real-time dashboards showing campaign ROI, lead velocity, and revenue attribution.',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    span: 'col-span-1 row-span-1',
  },
  {
    id: 'f4',
    icon: Globe,
    title: 'Website Builder',
    desc: 'Launch conversion-optimized landing pages in minutes with no-code templates.',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    span: 'col-span-1 row-span-1',
  },
  {
    id: 'f5',
    icon: MessageSquare,
    title: 'Campaign Automation',
    desc: 'Email, SMS, and social campaigns that run on autopilot based on lead behavior.',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    span: 'col-span-1 row-span-1',
  },
  {
    id: 'f6',
    icon: Calendar,
    title: 'Appointment Booking',
    desc: 'Sync calendars, send reminders, and reduce no-shows with automated scheduling.',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    span: 'col-span-1 row-span-1',
  },
];

const STEPS = [
  {
    id: 's1',
    num: '01',
    title: 'Connect your channels',
    desc: 'Link your phone number, website, social profiles, and ad accounts in under 10 minutes.',
  },
  {
    id: 's2',
    num: '02',
    title: 'AI learns your business',
    desc: 'Our AI studies your services, pricing, and FAQs to handle inquiries exactly as you would.',
  },
  {
    id: 's3',
    num: '03',
    title: 'Leads flow in automatically',
    desc: 'Every call, form fill, and chat is captured, qualified, and routed to your pipeline.',
  },
  {
    id: 's4',
    num: '04',
    title: 'Close more deals',
    desc: 'Follow-up sequences, appointment reminders, and revenue tracking close the loop.',
  },
];

const PLANS = [
  {
    id: 'p1',
    name: 'Starter',
    price: '$97',
    period: '/mo',
    desc: 'Perfect for solo operators and new businesses.',
    features: [
      'AI Receptionist (500 calls/mo)',
      'Up to 500 contacts',
      'Email campaigns',
      '1 website',
      'Basic analytics',
    ],
    cta: 'Start free trial',
    highlight: false,
  },
  {
    id: 'p2',
    name: 'Pro',
    price: '$247',
    period: '/mo',
    desc: 'For growing businesses ready to scale.',
    features: [
      'AI Receptionist (unlimited calls)',
      'Unlimited contacts',
      'Email + SMS campaigns',
      '5 websites',
      'Advanced analytics & attribution',
      'CRM pipeline & automation',
    ],
    cta: 'Start free trial',
    highlight: true,
  },
  {
    id: 'p3',
    name: 'Agency',
    price: '$497',
    period: '/mo',
    desc: 'Manage multiple clients from one platform.',
    features: [
      'Everything in Pro',
      'Unlimited client accounts',
      'White-label dashboard',
      'Priority support',
      'Custom integrations',
      'Dedicated onboarding',
    ],
    cta: 'Talk to sales',
    highlight: false,
  },
];

const TESTIMONIALS = [
  {
    id: 't1',
    name: 'Rachel Torres',
    role: 'Owner, Torres HVAC',
    quote:
      'We were missing 30% of our inbound calls. After MBS, our AI receptionist books jobs around the clock. Revenue up 40% in 3 months.',
    stars: 5,
    initials: 'RT',
    color: 'bg-blue-600',
  },
  {
    id: 't2',
    name: 'Marcus Webb',
    role: 'Founder, Webb Law Group',
    quote:
      'The lead qualification alone saves my team 15 hours a week. Every consultation is pre-screened and booked before I even see the name.',
    stars: 5,
    initials: 'MW',
    color: 'bg-violet-600',
  },
  {
    id: 't3',
    name: 'Diane Okafor',
    role: 'Director, Okafor Dental',
    quote:
      'We replaced 4 separate tools with MBS. The website builder, CRM, and appointment system all talk to each other. It just works.',
    stars: 5,
    initials: 'DO',
    color: 'bg-emerald-600',
  },
];

const STATS = [
  { id: 'st1', value: '12,400+', label: 'Leads Captured Monthly' },
  { id: 'st2', value: '840+', label: 'Active Businesses' },
  { id: 'st3', value: '99.9%', label: 'Platform Uptime' },
  { id: 'st4', value: '4.2×', label: 'Average ROI' },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── NAV ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-card/95 backdrop-blur-md border-b border-border shadow-topbar' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl gradient-brand flex items-center justify-center shrink-0">
              <span className="text-white font-extrabold text-xs">MBS</span>
            </div>
            <span className="font-bold text-foreground text-sm leading-tight hidden sm:block">
              Marketing Biz Solutions
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS?.map((l) => (
              <a
                key={l?.href}
                href={l?.href}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-all duration-150"
              >
                {l?.label}
              </a>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/auth/login"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </Link>
            <Link
              href="/auth/login"
              className="btn-primary text-sm"
            >
              Get Started Free
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-card border-b border-border px-4 pb-4 space-y-1">
            {NAV_LINKS?.map((l) => (
              <a
                key={l?.href}
                href={l?.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted"
              >
                {l?.label}
              </a>
            ))}
            <div className="pt-2 flex flex-col gap-2">
              <Link href="/auth/login" className="btn-secondary justify-center text-sm">
                Sign In
              </Link>
              <Link href="/auth/login" className="btn-primary justify-center text-sm">
                Get Started Free <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </header>
      {/* ── HERO ── */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background gradient blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-primary/8 blur-3xl" />
          <div className="absolute top-20 -left-40 w-[500px] h-[500px] rounded-full bg-accent/6 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] rounded-full bg-primary/4 blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-6">
            <Zap size={12} className="fill-current" />
            AI-Powered Marketing Platform
          </div>

          <h1 className="text-hero-xl font-extrabold text-foreground leading-tight mb-6 tracking-tight">
            Stop losing customers to{' '}
            <span className="relative">
              <span className="relative z-10 text-transparent bg-clip-text gradient-brand">
                missed calls
              </span>
            </span>{' '}
            and slow follow-ups
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Marketing Biz Solutions replaces your receptionist, CRM, and campaign tools with one AI platform that captures every lead, books every appointment, and grows your revenue — automatically.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              href="/auth/login"
              className="btn-primary px-7 py-3 text-base rounded-xl shadow-card-hover"
            >
              Start your free trial
              <ArrowRight size={16} />
            </Link>
            <a
              href="#how-it-works"
              className="btn-secondary px-7 py-3 text-base rounded-xl"
            >
              See how it works
              <ChevronDown size={16} />
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            {['No credit card required', '14-day free trial', 'Cancel anytime', 'SOC 2 compliant']?.map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-emerald-500" />
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative max-w-4xl mx-auto mt-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden shadow-card">
            {STATS?.map((s) => (
              <div key={s?.id} className="bg-card px-6 py-5 text-center">
                <p className="text-2xl font-extrabold text-foreground font-mono">{s?.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── FEATURES BENTO ── */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/40">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Platform Features</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground leading-tight max-w-xl">
              Eight tools replaced by one platform
            </h2>
          </div>

          {/* Bento grid — asymmetric */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
            {/* Large card — AI Receptionist */}
            <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-7 shadow-card hover:shadow-card-hover transition-shadow duration-200 group">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-200">
                  <PhoneCall size={22} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg mb-2">AI Receptionist</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Answers every call 24/7, qualifies leads, and books appointments — without hiring staff. Handles FAQs, pricing questions, and scheduling in a natural conversation.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {['24/7 availability', 'Natural language', 'Auto-booking', 'Call transcripts']?.map((tag) => (
                  <span key={tag} className="badge-info text-xs">{tag}</span>
                ))}
              </div>
            </div>

            {/* Tall card — Lead Management */}
            <div className="row-span-2 bg-card border border-border rounded-2xl p-7 shadow-card hover:shadow-card-hover transition-shadow duration-200 group flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0 mb-4 group-hover:scale-105 transition-transform duration-200">
                <Users size={22} className="text-violet-600" />
              </div>
              <h3 className="font-bold text-foreground text-lg mb-2">Lead Management</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Capture, score, and nurture leads from every channel in one unified CRM pipeline. Never let a hot lead go cold.
              </p>
              <div className="flex-1 bg-muted/60 rounded-xl p-4 space-y-2.5">
                {[
                  { label: 'New Leads', val: '48', color: 'bg-blue-500' },
                  { label: 'Qualified', val: '31', color: 'bg-violet-500' },
                  { label: 'Booked', val: '19', color: 'bg-emerald-500' },
                  { label: 'Closed', val: '12', color: 'bg-amber-500' },
                ]?.map((row) => (
                  <div key={row?.label} className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${row?.color} shrink-0`} />
                    <span className="text-xs text-muted-foreground flex-1">{row?.label}</span>
                    <span className="text-xs font-bold text-foreground font-mono">{row?.val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Regular cards */}
            {[
              { id: 'f3', icon: BarChart3, title: 'Live Analytics', desc: 'Real-time dashboards showing campaign ROI, lead velocity, and revenue attribution.', iconColor: 'text-emerald-600', bg: 'bg-emerald-50' },
              { id: 'f4', icon: Globe, title: 'Website Builder', desc: 'Launch conversion-optimized landing pages in minutes with no-code templates.', iconColor: 'text-amber-600', bg: 'bg-amber-50' },
              { id: 'f5', icon: MessageSquare, title: 'Campaign Automation', desc: 'Email, SMS, and social campaigns that run on autopilot based on lead behavior.', iconColor: 'text-rose-600', bg: 'bg-rose-50' },
              { id: 'f6', icon: Calendar, title: 'Appointment Booking', desc: 'Sync calendars, send reminders, and reduce no-shows with automated scheduling.', iconColor: 'text-cyan-600', bg: 'bg-cyan-50' },
            ]?.map((f) => {
              const Icon = f?.icon;
              return (
                <div key={f?.id} className="bg-card border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-200 group">
                  <div className={`w-10 h-10 rounded-xl ${f?.bg} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-200`}>
                    <Icon size={18} className={f?.iconColor} />
                  </div>
                  <h3 className="font-bold text-foreground text-base mb-1.5">{f?.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{f?.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Setup Process</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground leading-tight max-w-xl">
              Live in under 30 minutes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS?.map((step, i) => (
              <div key={step?.id} className="relative">
                {/* Connector line */}
                {i < STEPS?.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-[calc(100%-1rem)] w-8 h-px bg-border z-10" />
                )}
                <div className="bg-card border border-border rounded-2xl p-6 shadow-card h-full">
                  <div className="w-12 h-12 rounded-xl gradient-brand flex items-center justify-center mb-4">
                    <span className="text-white font-extrabold text-sm font-mono">{step?.num}</span>
                  </div>
                  <h3 className="font-bold text-foreground text-base mb-2">{step?.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step?.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/40">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground leading-tight mb-3">
              Transparent, all-inclusive pricing
            </h2>
            <p className="text-muted-foreground text-base max-w-lg mx-auto">
              No per-seat fees, no hidden add-ons. Every plan includes the full platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {PLANS?.map((plan) => (
              <div
                key={plan?.id}
                className={`rounded-2xl p-7 border shadow-card transition-shadow duration-200 hover:shadow-card-hover relative ${
                  plan?.highlight
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-card border-border'
                }`}
              >
                {plan?.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                    Most Popular
                  </div>
                )}
                <p className={`text-sm font-semibold mb-1 ${plan?.highlight ? 'text-background/70' : 'text-muted-foreground'}`}>
                  {plan?.name}
                </p>
                <div className="flex items-end gap-1 mb-2">
                  <span className={`text-4xl font-extrabold font-mono ${plan?.highlight ? 'text-background' : 'text-foreground'}`}>
                    {plan?.price}
                  </span>
                  <span className={`text-sm mb-1 ${plan?.highlight ? 'text-background/60' : 'text-muted-foreground'}`}>
                    {plan?.period}
                  </span>
                </div>
                <p className={`text-sm mb-6 ${plan?.highlight ? 'text-background/70' : 'text-muted-foreground'}`}>
                  {plan?.desc}
                </p>
                <ul className="space-y-2.5 mb-7">
                  {plan?.features?.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2
                        size={15}
                        className={`shrink-0 mt-0.5 ${plan?.highlight ? 'text-background/80' : 'text-emerald-500'}`}
                      />
                      <span className={plan?.highlight ? 'text-background/90' : 'text-foreground'}>
                        {feat}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link
                  href="/auth/login"
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-95 ${
                    plan?.highlight
                      ? 'bg-background text-foreground hover:bg-background/90'
                      : 'btn-primary'
                  }`}
                >
                  {plan?.cta}
                  <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Customer Stories</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground leading-tight max-w-xl">
              Real results from real businesses
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS?.map((t) => (
              <div key={t?.id} className="bg-card border border-border rounded-2xl p-7 shadow-card hover:shadow-card-hover transition-shadow duration-200 flex flex-col">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t?.stars })?.map((_, i) => (
                    <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-foreground text-sm leading-relaxed flex-1 mb-5">
                  &ldquo;{t?.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full ${t?.color} flex items-center justify-center shrink-0`}>
                    <span className="text-white text-xs font-bold">{t?.initials}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t?.name}</p>
                    <p className="text-xs text-muted-foreground">{t?.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── CTA BANNER ── */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="gradient-brand rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white text-xs font-semibold mb-5">
                <Shield size={12} />
                14-day free trial · No credit card required
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
                Ready to stop missing leads?
              </h2>
              <p className="text-blue-100 text-base max-w-lg mx-auto mb-8">
                Join 840+ businesses using Marketing Biz Solutions to capture every lead and grow revenue on autopilot.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href="/auth/login"
                  className="inline-flex items-center gap-2 bg-white text-foreground px-8 py-3 rounded-xl text-sm font-bold hover:bg-white/90 active:scale-95 transition-all duration-150 shadow-card-hover"
                >
                  Create your free account
                  <ArrowRight size={15} />
                </Link>
                <Link
                  href="/auth/login"
                  className="inline-flex items-center gap-2 text-white border border-white/30 px-8 py-3 rounded-xl text-sm font-semibold hover:bg-white/10 active:scale-95 transition-all duration-150"
                >
                  Sign in to your account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── FOOTER ── */}
      <footer className="border-t border-border py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg gradient-brand flex items-center justify-center shrink-0">
              <span className="text-white font-extrabold text-xs">MBS</span>
            </div>
            <span className="text-sm font-semibold text-foreground">Marketing Biz Solutions</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
            <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-foreground transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-foreground transition-colors">Support</a>
            <Link href="/auth/login" className="hover:text-foreground transition-colors">Sign In</Link>
          </div>
          <p className="text-xs text-muted-foreground">
            © 2026 Marketing Biz Solutions
          </p>
        </div>
      </footer>
    </div>
  );
}
